import { createHttpClient } from '@adapters/HttpClient';
import { CategoryApiAdapter } from '@modules/Products/CreateEdit/adapters/CategoryApiAdapter';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { TeamsApiAdapter } from '../adapters/TeamsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

interface ProductsFiltersState {
  code: string;
  createdAt: DateRange | undefined;
  client: string[];
  category: string[];
  // Filtros numéricos (rangos)
  cost: { min: string; max: string };
  available: { min: string; max: string };
  reserved: { min: string; max: string };
  stock: { min: string; max: string };
}

interface UseProductsFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  hasTeamData?: boolean;
}

export const useProductsFilters = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
}: UseProductsFiltersProps) => {
  // Estado inicial
  const [filters, setFilters] = useState<ProductsFiltersState>(() => {
    return parseConditionsToState(initialConditions);
  });

  const httpClient = createHttpClient(apiClientWH);
  const teamsAdapter = useMemo(() => new TeamsApiAdapter(httpClient), [httpClient]);

  // Cargar clientes (teams)
  const { data: clients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['products-filters-clients'],
    queryFn: async () => {
      if (!hasTeamData) return [];
      const teams = await teamsAdapter.getTeamsChild();
      return teams.map((team) => ({ value: team.id.toString(), label: team.name }));
    },
    enabled: hasTeamData,
  });

  const categoryAdapter = useMemo(() => new CategoryApiAdapter(httpClient), [httpClient]);

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery({
    queryKey: ['products-filters-categories'],
    queryFn: async () => {
      const list = await categoryAdapter.getAll();
      return list.map((cat) => ({ value: cat.id, label: cat.name }));
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Convertir estado a condiciones API
  const convertStateToConditions = useCallback((state: ProductsFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    // Code (contains)
    if (state.code?.trim()) {
      conditions.push({
        column: 'code',
        action: 'contains',
        value: state.code.trim(),
      });
    }

    // Created At (Date Range)
    if (state.createdAt?.from && state.createdAt?.to) {
      conditions.push({
        column: 'created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.createdAt.from),
      });
      conditions.push({
        column: 'created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.createdAt.to),
      });
    }

    // Client (in)
    if (state.client.length > 0) {
      conditions.push({
        column: 'team_id',
        action: 'in',
        value: state.client,
      });
    }

    // Category (in)
    if (state.category.length > 0) {
      conditions.push({
        column: 'category_id',
        action: 'in',
        value: state.category,
      });
    }

    // Cost (Range)
    if (state.cost.min) {
      conditions.push({ column: 'cost', action: 'greater_equal', value: state.cost.min });
    }
    if (state.cost.max) {
      conditions.push({ column: 'cost', action: 'less_equal', value: state.cost.max });
    }

    // Available (Range)
    if (state.available.min) {
      conditions.push({ column: 'stock.disponible', action: 'greater_equal', value: state.available.min });
    }
    if (state.available.max) {
      conditions.push({ column: 'stock.disponible', action: 'less_equal', value: state.available.max });
    }

    // Reserved (Range)
    if (state.reserved.min) {
      conditions.push({ column: 'stock.reservado', action: 'greater_equal', value: state.reserved.min });
    }
    if (state.reserved.max) {
      conditions.push({ column: 'stock.reservado', action: 'less_equal', value: state.reserved.max });
    }

    // Total Stock (Range)
    if (state.stock.min) {
      conditions.push({ column: 'stock.total', action: 'greater_equal', value: state.stock.min });
    }
    if (state.stock.max) {
      conditions.push({ column: 'stock.total', action: 'less_equal', value: state.stock.max });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<ProductsFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        const conditions = convertStateToConditions(newState);
        onChange?.(conditions);
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  // Handlers
  const handleCodeChange = (value: string) => updateFilters({ code: value });
  const handleCreatedAtChange = (range: DateRange | undefined) => updateFilters({ createdAt: range });
  const handleClientChange = (values: string[]) => updateFilters({ client: values });
  const handleCategoryChange = (values: string[]) => updateFilters({ category: values });

  const handleCostChange = (type: 'min' | 'max', value: string) =>
    updateFilters({ cost: { ...filters.cost, [type]: value } });

  const handleAvailableChange = (type: 'min' | 'max', value: string) =>
    updateFilters({ available: { ...filters.available, [type]: value } });

  const handleReservedChange = (type: 'min' | 'max', value: string) =>
    updateFilters({ reserved: { ...filters.reserved, [type]: value } });

  const handleStockChange = (type: 'min' | 'max', value: string) =>
    updateFilters({ stock: { ...filters.stock, [type]: value } });

  const clearAllFilters = useCallback(() => {
    const emptyState: ProductsFiltersState = {
      code: '',
      createdAt: undefined,
      client: [],
      category: [],
      cost: { min: '', max: '' },
      available: { min: '', max: '' },
      reserved: { min: '', max: '' },
      stock: { min: '', max: '' },
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  // Sincronización inicial
  useEffect(() => {
    const newState = parseConditionsToState(initialConditions);
    setFilters(newState);
  }, [initialConditions]);

  return {
    filters,
    handleCodeChange,
    handleCreatedAtChange,
    handleClientChange,
    handleCategoryChange,
    handleCostChange,
    handleAvailableChange,
    handleReservedChange,
    handleStockChange,
    clearAllFilters,
    clientOptions: clients,
    isLoadingClients,
    categoryOptions: categories,
    isLoadingCategories,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): ProductsFiltersState {
  const state: ProductsFiltersState = {
    code: '',
    createdAt: undefined,
    client: [],
    category: [],
    cost: { min: '', max: '' },
    available: { min: '', max: '' },
    reserved: { min: '', max: '' },
    stock: { min: '', max: '' },
  };

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  // Code
  const codeCond = byColumn['code']?.find((c) => c.action === 'contains');
  if (codeCond) state.code = codeCond.value as string;

  // Client
  const clientCond = byColumn['team_id']?.find((c) => c.action === 'in');
  if (clientCond) state.client = Array.isArray(clientCond.value) ? clientCond.value : [clientCond.value as string];

  // Category
  const categoryCond = byColumn['category_id']?.find((c) => c.action === 'in');
  if (categoryCond) {
    state.category = Array.isArray(categoryCond.value) ? categoryCond.value.map(String) : [String(categoryCond.value)];
  }

  // Dates
  const createdFrom = byColumn['created_at']?.find((c) => c.action === 'greater_equal');
  const createdTo = byColumn['created_at']?.find((c) => c.action === 'less_equal');
  if (createdFrom?.value && createdTo?.value) {
    state.createdAt = { from: new Date(createdFrom.value as string), to: new Date(createdTo.value as string) };
  }

  // Ranges helper
  const setRange = (col: string, target: { min: string; max: string }) => {
    const min = byColumn[col]?.find((c) => c.action === 'greater_equal');
    const max = byColumn[col]?.find((c) => c.action === 'less_equal');
    if (min) target.min = min.value as string;
    if (max) target.max = max.value as string;
  };

  setRange('cost', state.cost);
  setRange('stock.disponible', state.available);
  setRange('stock.reservado', state.reserved);
  setRange('stock.total', state.stock);

  return state;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
